import React from 'react';
import { Helmet } from 'react-helmet-async';
import { seoConfig } from '../config/seoConfig';
import { siteWideSchemas } from '../lib/structuredData';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  ogImage?: string;
  canonical?: string;
  noindex?: boolean;
  nofollow?: boolean;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  type?: 'website' | 'article' | 'profile';
  structuredData?: object | object[];
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  ogImage,
  canonical,
  noindex = false,
  nofollow = false,
  author,
  publishedTime,
  modifiedTime,
  type = 'website',
  structuredData
}) => {
  const fullTitle = title
    ? title.includes('|')
      ? title
      : `${title} | Behandlung.de`
    : seoConfig.defaultTitle;

  const metaDescription = description || seoConfig.defaultDescription;
  const metaKeywords = keywords || seoConfig.defaultKeywords;
  const metaOgImage = ogImage ? `${seoConfig.siteUrl}${ogImage}` : `${seoConfig.siteUrl}${seoConfig.defaultOgImage}`;
  const canonicalUrl = canonical || (typeof window !== 'undefined' ? window.location.href : seoConfig.siteUrl);

  const robotsContent = [];
  if (noindex) robotsContent.push('noindex');
  if (nofollow) robotsContent.push('nofollow');
  if (robotsContent.length === 0) {
    robotsContent.push('index', 'follow');
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords.join(', ')} />
      <meta name="robots" content={robotsContent.join(', ')} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={metaOgImage} />
      <meta property="og:site_name" content="Behandlung.de" />
      <meta property="og:locale" content="de_DE" />

      {author && <meta property="article:author" content={author} />}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaOgImage} />

      <meta name="theme-color" content="#2563eb" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Behandlung.de" />

      <script type="application/ld+json">
        {JSON.stringify([
          ...siteWideSchemas(),
          ...(structuredData
            ? Array.isArray(structuredData) ? structuredData : [structuredData]
            : []),
        ])}
      </script>
    </Helmet>
  );
};

export default SEO;
